import { useState, useEffect } from "react";
import dayjs, { Dayjs } from "../utils/dayjsConfig";
import { getHolidays } from "../connectors/operationApi";

const ARGENTINA_TZ = "America/Argentina/Buenos_Aires";
const MARKET_OPEN_HOUR = 10;
const MARKET_OPEN_MINUTE = 30;
const MARKET_CLOSE_HOUR = 17;
const MARKET_CLOSE_MINUTE = 0;

const REPO_CLOSE_HOUR = 17;
const REPO_CLOSE_MINUTE = 30;

const WEEKDAY_SATURDAY = 6;
const WEEKDAY_SUNDAY = 0;

const UPDATE_INTERVAL = 1000;

const isWeekend = (date: Dayjs): boolean => {
  const day = date.day();
  return day === WEEKDAY_SATURDAY || day === WEEKDAY_SUNDAY;
};

const isWithinMarketHours = (now: Dayjs): boolean => {
  const marketOpen = now
    .hour(MARKET_OPEN_HOUR)
    .minute(MARKET_OPEN_MINUTE)
    .second(0);
  const marketClose = now
    .hour(MARKET_CLOSE_HOUR)
    .minute(MARKET_CLOSE_MINUTE)
    .second(0);

  return now.isSameOrAfter(marketOpen) && now.isBefore(marketClose);
};

const isWithinRepoHours = (now: Dayjs): boolean => {
  const marketOpen = now
    .hour(MARKET_OPEN_HOUR)
    .minute(MARKET_OPEN_MINUTE)
    .second(0);
  const repoClose = now
    .hour(REPO_CLOSE_HOUR)
    .minute(REPO_CLOSE_MINUTE)
    .second(0);

  return now.isSameOrAfter(marketOpen) && now.isBefore(repoClose);
};

export const useIsMarketOpen = () => {
  const [isMarketOpen, setIsMarketOpen] = useState(false);
  const [isHolidayToday, setIsHolidayToday] = useState(false);
  const [isRepoOperationOpen, setIsRepoOperationOpen] = useState(false);
  const [marketCanOperateT1Today, setMarketCanOperateT1Today] = useState(false);
  const [nextBusinessDay, setNextBusinessDay] = useState("");
  const [followingBusinessDay, setFollowingBusinessDay] = useState("");

  useEffect(() => {
    let isMounted = true;

    const fetchHolidays = async () => {
      try {
        const data = await getHolidays();

        if (isMounted) {
          setIsHolidayToday(data.is_holiday_today);
          setMarketCanOperateT1Today(data.market_can_operate_t1_today);
          setNextBusinessDay(data.next_business_day);
          setFollowingBusinessDay(data.following_business_day);
        }
      } catch (error) {
        console.error("Error fetching holidays:", error);

        if (isMounted) {
          setIsHolidayToday(false);
          setMarketCanOperateT1Today(false);
        }
      }
    };

    fetchHolidays();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const checkMarketStatus = () => {
      const now = dayjs().tz(ARGENTINA_TZ);

      const isWorkday =
        !isWeekend(now) &&
        (!isHolidayToday || (isHolidayToday && marketCanOperateT1Today));
      const withinHours = isWithinMarketHours(now);

      setIsMarketOpen(isWorkday && withinHours);
      setIsRepoOperationOpen(isWorkday && isWithinRepoHours(now));
    };

    checkMarketStatus();

    const interval = setInterval(checkMarketStatus, UPDATE_INTERVAL);

    return () => clearInterval(interval);
  }, [isHolidayToday, marketCanOperateT1Today]);

  return {
    isMarketOpen,
    isRepoOperationOpen,
    isHolidayToday,
    marketCanOperateT1Today,
    nextBusinessDay,
    followingBusinessDay,
  };
};
